export interface StorageProvider {
  set: (key: string, value: string) => void;
  getString: (key: string) => string | null;
  delete: (key: string) => void;
}

type AuthListener = () => void;

class TokenManager {
  private static storage: StorageProvider | null = null;

  private static TOKEN_KEY = 'set_cookie';
  private static AUTH_TOKEN_KEY = 'token';
  private static authListener: AuthListener | null = null;

  private static clientVersion: string | null = null;
  private static clientType: string | null = null;

  static setClientVersion(clientVersion: string | null): void {
    if (clientVersion) {
      this.clientVersion = clientVersion;
    }
  }

  static setClientType(clientType: string | null): void {
    if (clientType) {
      this.clientType = clientType;
    }
  }

  static getClientVersion(): string | null {
    return this.clientVersion;
  }

  static getClientType(): string | null {
    return this.clientType
  }

  static setAuthListener(listener: AuthListener | null): void {
    if (listener) {
      this.authListener = listener;
    }
  }

  static removeAuthListener(): void {
    this.authListener = null;
  }

  static notifyListener(status: boolean): void {
    if (status && this.authListener) {
      this.authListener();
    }
  }

  static setStorageProvider(provider: StorageProvider): void {
    if (
      !provider ||
      typeof provider.set !== 'function' ||
      typeof provider.getString !== 'function' ||
      typeof provider.delete !== 'function'
    ) {
      throw new Error(
        'Invalid storage provider. Must implement StorageProvider interface.',
      );
    }
    this.storage = provider;
  }

  private static getStorage(): StorageProvider {
    if (!this.storage) {
      throw new Error(
        'Storage provider not set. Use setStorageProvider() first.',
      );
    }
    return this.storage;
  }

  private static get(key: string): string | null {
    try {
      return this.getStorage().getString(key) || null;
    } catch (error) {
      console.error('Failed to get token', error);
      return null;
    }
  }

  private static set(key: string, value: string): void {
    try {
      this.getStorage().set(key, value);
    } catch (error) {
      console.error(`Failed to set ${key}`, error);
    }
  }

  private static remove(key: string): void {
    try {
      this.getStorage().delete(key);
    } catch (error) {
      console.error(`Failed to set ${key}`, error);
    }
  }

  static setToken(token: string): void {
    this.set(this.TOKEN_KEY, token);
  }

  static getToken(): string | null {
    return this.get(this.TOKEN_KEY);
  }

  static setAuthToken(token: string): void {
    this.set(this.AUTH_TOKEN_KEY, token);
  }

  static getAuthToken(): string | null {
    return this.get(this.AUTH_TOKEN_KEY);
  }

  static removeAuthToken(): void {
    this.remove(this.AUTH_TOKEN_KEY);
  }

  static removeToken(): void {
    this.remove(this.TOKEN_KEY);
  }
}

export default TokenManager;
